// src/realtime/logtaghub.ts
import * as signalR from "@microsoft/signalr";

/**
 * Singleton SignalR connection:
 * - Tránh tạo nhiều connection khi React StrictMode render 2 lần
 * - Tự reset connection nếu đổi hubUrl/options
 * - Mặc định dùng negotiation + WebSockets + LongPolling fallback
 * - Local dev/IIS Express ổn hơn so với ép skipNegotiation + WebSockets only
 */

let _conn: signalR.HubConnection | null = null;
let _connKey: string | null = null;
let _starting: Promise<void> | null = null;

export type HubOptions = {
  /** Ép LongPolling nếu WebSocket local bị lỗi */
  forceLongPolling?: boolean;

  /** Ép WebSocket only, chỉ nên dùng khi server/proxy chắc chắn hỗ trợ */
  forceWebSockets?: boolean;
};

const sleep = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

function buildConnectionKey(hubUrl: string, opts?: HubOptions) {
  return JSON.stringify({
    hubUrl: hubUrl.replace(/\/+$/, ""),
    forceLongPolling: !!opts?.forceLongPolling,
    forceWebSockets: !!opts?.forceWebSockets,
  });
}

export async function resetLogTagConnection() {
  if (_conn) {
    try {
      await _conn.stop();
    } catch {
      // ignore
    }
  }

  _conn = null;
  _connKey = null;
  _starting = null;
}

/** Khởi tạo/lấy singleton connection */
export function getLogTagConnection(
  hubUrl: string,
  accessToken?: () => string | null,
  opts?: HubOptions
): signalR.HubConnection {
  const cleanUrl = hubUrl.replace(/\/+$/, "");
  const nextKey = buildConnectionKey(cleanUrl, opts);

  // Nếu cùng URL/options thì dùng lại connection cũ
  if (_conn && _connKey === nextKey) return _conn;

  // Nếu đổi URL/options thì bỏ connection cũ để tránh giữ nhầm endpoint
  if (_conn && _connKey !== nextKey) {
    try {
      _conn.stop().catch(() => {});
    } catch {
      // ignore
    }

    _conn = null;
    _starting = null;
    _connKey = null;
  }

  let transport: signalR.HttpTransportType;

  if (opts?.forceLongPolling) {
    transport = signalR.HttpTransportType.LongPolling;
  } else if (opts?.forceWebSockets) {
    transport = signalR.HttpTransportType.WebSockets;
  } else {
    // Mặc định: cho SignalR negotiation và fallback
    transport =
      signalR.HttpTransportType.WebSockets |
      signalR.HttpTransportType.LongPolling;
  }

  const httpOptions: signalR.IHttpConnectionOptions = {
    withCredentials: true,
    transport,
    ...(accessToken ? { accessTokenFactory: () => accessToken() ?? "" } : {}),

    // Chỉ skipNegotiation khi ép WebSocket only
    ...(opts?.forceWebSockets
      ? { skipNegotiation: true }
      : {}),
  };

  _conn = new signalR.HubConnectionBuilder()
    .withUrl(cleanUrl, httpOptions)
    .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
    .configureLogging(signalR.LogLevel.Warning)
    .build();

  _conn.keepAliveIntervalInMilliseconds = 15000;
  _conn.serverTimeoutInMilliseconds = 60000;

  _conn.onclose((err) => {
    if (err) console.warn("[hub] closed with error:", err);
    else console.log("[hub] closed");
  });

  _conn.onreconnecting((err) => {
    console.warn("[hub] reconnecting:", err);
  });

  _conn.onreconnected((connectionId) => {
    console.log("[hub] reconnected:", connectionId);
  });

  _connKey = nextKey;
  return _conn;
}

/**
 * Đảm bảo connection đã start
 */
export async function ensureStarted(
  conn: signalR.HubConnection,
  timeoutMs: number = 15000,
  maxRetries: number = 2
): Promise<void> {
  if (conn.state === signalR.HubConnectionState.Connected) return;
  if (_starting) return _starting;

  const startOnce = async () => {
    if (
      conn.state === signalR.HubConnectionState.Connecting ||
      conn.state === signalR.HubConnectionState.Reconnecting
    ) {
      await sleep(300);
    }

    if (conn.state === signalR.HubConnectionState.Connected) return;

    if (conn.state !== signalR.HubConnectionState.Disconnected) {
      try {
        await conn.stop();
      } catch {
        // ignore
      }
      await sleep(300);
    }

    const startPromise = conn.start();

    await Promise.race([
      startPromise,
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error("hub start timeout")), timeoutMs)
      ),
    ]);
  };

  _starting = (async () => {
    try {
      let attempt = 0;

      while (true) {
        try {
          await startOnce();
          console.log("[hub] ✅ connected");
          return;
        } catch (err) {
          attempt++;
          console.warn(`[hub] start failed (attempt ${attempt})`, err);

          if (attempt > maxRetries) {
            throw err;
          }

          const delay = Math.min(2000 * attempt, 5000);
          await sleep(delay);
        }
      }
    } finally {
      _starting = null;
    }
  })();

  return _starting;
}

/** Dừng connection an toàn */
export async function safeStop() {
  await resetLogTagConnection();
  console.log("[hub] ⏹ stopped");
}

/** Truy cập raw connection */
export function currentConnection(): signalR.HubConnection | null {
  return _conn;
}